/**
 * Lightweight query cache for Life4Earth V2.
 *
 * Provides TanStack-Query-like semantics without an npm dependency:
 *   - Cached data with configurable stale time
 *   - Request deduplication (in-flight promise sharing)
 *   - Selective invalidation (mark stale, refetch only if subscribers exist)
 *   - Window-event-based subscriber notification
 *
 * Architecture:
 *   - Module-level cache (singleton) shared across all hooks
 *   - Each cache entry keyed by a string (e.g. "schedule", "finance")
 *   - useApiQuery() hook subscribes to a cache key and returns the
 *     same { data, loading, error, source, refetch } shape as the
 *     existing hooks — zero changes needed downstream
 *
 * This replaces the old pattern of:
 *   useState + useEffect + fetchCount → fetch → setState
 * with:
 *   queryCache.fetch(key) → cache hit (fresh) → return cached
 *                         → cache miss/stale → deduplicated fetch → update all subscribers
 */

/* ================================================================
   Types
   ================================================================ */

interface CacheEntry<T = any> {
  data: T | undefined;
  error: string | null;
  /** Timestamp (ms) when data was last successfully fetched */
  fetchedAt: number;
  /** In-flight promise (for deduplication) */
  inflight: Promise<T> | null;
  /** Source of the data */
  source: string;
}

interface CacheOptions {
  /**
   * How long (ms) fetched data is considered fresh.
   * Default: 60_000 (1 minute).
   * During this window, refetch() is a no-op unless force=true.
   */
  staleTime?: number;
}

/* ================================================================
   Cache store (module-level singleton)
   ================================================================ */

const cache = new Map<string, CacheEntry>();

const DEFAULT_STALE_TIME = 60_000; // 1 minute

/** Per-key stale time overrides */
const staleTimeOverrides = new Map<string, number>();

/* ================================================================
   Custom event for notifying React hooks of cache updates
   ================================================================ */

const CACHE_UPDATE_EVENT = "l4e_query_cache_update";

function notifySubscribers(key: string) {
  window.dispatchEvent(
    new CustomEvent(CACHE_UPDATE_EVENT, { detail: { key } })
  );
}

/* ================================================================
   Core cache operations
   ================================================================ */

function getEntry<T>(key: string): CacheEntry<T> {
  if (!cache.has(key)) {
    cache.set(key, {
      data: undefined,
      error: null,
      fetchedAt: 0,
      inflight: null,
      source: "loading",
    });
  }
  return cache.get(key) as CacheEntry<T>;
}

function isFresh(key: string): boolean {
  const entry = cache.get(key);
  if (!entry || !entry.fetchedAt) return false;
  const staleTime = staleTimeOverrides.get(key) ?? DEFAULT_STALE_TIME;
  return Date.now() - entry.fetchedAt < staleTime;
}

/**
 * Execute a fetch for the given cache key.
 * Deduplicates: if a fetch is already in-flight, returns the same promise.
 * Respects stale time: if data is fresh, returns immediately (unless force=true).
 */
export async function fetchForKey<T>(
  key: string,
  fetchFn: () => Promise<T>,
  options?: { force?: boolean; fallbackFn?: () => T }
): Promise<T> {
  const entry = getEntry<T>(key);

  // If data is fresh and not forced, return cached
  if (!options?.force && isFresh(key) && entry.data !== undefined) {
    return entry.data;
  }

  // If there's already an in-flight request, return it (deduplication)
  if (entry.inflight) {
    return entry.inflight;
  }

  // Start a new fetch
  const promise = (async () => {
    try {
      const result = await fetchFn();
      entry.data = result;
      entry.error = null;
      entry.fetchedAt = Date.now();
      entry.source = "api";
      entry.inflight = null;
      // No notifySubscribers() here — the calling hook already handles
      // the result via its .then() callback. Only invalidateAndRefetch()
      // should dispatch CACHE_UPDATE_EVENT to trigger refetches.
      return result;
    } catch (err: any) {
      entry.inflight = null;

      // Try fallback
      if (options?.fallbackFn) {
        try {
          const fallback = options.fallbackFn();
          entry.data = fallback;
          entry.error = err.message;
          entry.source = "seed";
          entry.fetchedAt = Date.now();
          return fallback;
        } catch {
          // Fallback also failed
        }
      }

      entry.error = err.message;
      entry.source = "seed";
      throw err;
    }
  })();

  entry.inflight = promise;
  return promise;
}

/**
 * Mark a cache key as stale (fetchedAt = 0).
 * Does NOT trigger a refetch — the next subscriber render or
 * explicit refetch() call will trigger the actual fetch.
 */
export function invalidate(key: string): void {
  const entry = cache.get(key);
  if (entry) {
    entry.fetchedAt = 0;
  }
}

/**
 * Mark a cache key as stale AND immediately trigger a refetch.
 * Used by SignalR event handlers.
 */
export function invalidateAndRefetch(key: string): void {
  invalidate(key);
  // Notify subscribers — their useEffect will see stale data and refetch
  notifySubscribers(key);
}

/**
 * DEV-849: Invalidate ALL cache entries whose key starts with a given prefix,
 * then notify subscribers under each affected key so they refetch.
 *
 * Why: writes that affect a CLASS of cached queries (e.g. all per-project HSE
 * incident lists, plus the bulk-fetch variant of the same data) need to clear
 * every related entry — not just a single key. Without this, fetching code
 * paths that use a different cache key shape (e.g. `hse-incidents-bulk:`)
 * keep returning their own stale data even after `invalidateAndRefetch`
 * cleared a single sibling key.
 *
 * Use sparingly — broad invalidation is expensive in network requests if
 * many queries share the prefix. Prefer single-key invalidation when the
 * caller knows the exact key.
 */
export function invalidateByPrefix(prefix: string): void {
  for (const [key, entry] of cache.entries()) {
    if (key.startsWith(prefix)) {
      entry.fetchedAt = 0;
      notifySubscribers(key);
    }
  }
}

/**
 * Set the stale time for a specific cache key.
 */
export function setStaleTime(key: string, ms: number): void {
  staleTimeOverrides.set(key, ms);
}

/**
 * Get the current cache entry for a key (for reading without subscribing).
 */
export function getCacheEntry<T>(key: string): CacheEntry<T> | undefined {
  return cache.get(key) as CacheEntry<T> | undefined;
}

/**
 * Get the custom event name for cache updates (used by useApiQuery).
 */
export { CACHE_UPDATE_EVENT };

/**
 * Clear all cache entries. Useful for testing or user logout.
 */
export function clearAll(): void {
  cache.clear();
}

/* ================================================================
   Well-known cache keys — single source of truth
   ================================================================ */

export const QUERY_KEYS = {
  projects: "projects",
  schedule: "schedule",
  finance: "finance",
  kpiConfigs: "kpiConfigs",
  curveMeta: "curveMeta",
  kpiProgress: "kpiProgress",
  mission: "mission",
  workflows: "workflows",
  community: "community",
  security: "security",
  preflight: "preflight",
  gantt: "gantt",
  workspaceAi: "workspaceAi",
  impactSignature: "impactSignature",
  projectPartners: "projectPartners",
  projectSections: "projectSections",
} as const;

export type QueryKey = (typeof QUERY_KEYS)[keyof typeof QUERY_KEYS];
